<template>
  <div
    :class="{ 'm-t-lg': !openAttentionCases, 'm-t-md': openAttentionCases }"
    class="container"
  >
    <div class="columns">
      <div class="column">
        <div class="content">
          <h3>{{ $wording.home.title_1 }}</h3>
          <div class="notification is-light is-info">
            <p>
              Use this site to complete required lead disclosures and certifications for rental properties and family daycare facilities. Learn more about the <a
                href="https://www.phila.gov/programs/lead-and-healthy-homes-program/"
                target="_blank"
              >Lead and Healthy Homes Program <i class="fas fa-external-link" /></a> and property owners' certification requirements on our website.
            </p>   
            <p>To begin:</p>
            <ul>
              <li>
                Search for your property below to start a lead disclosure process. This will determine if the property is subject to additional <a
                  href="https://www.phila.gov/services/permits-violations-licenses/apply-for-a-permit/environmental-permits-and-approvals/submit-lead-certification-and-inspection-reports/"
                  target="_blank"
                >certification requirements <i class="fas fa-external-link" /></a>. You can save these results and return to complete additional requirements later.
              </li>
              <li>
                If your property is required to be certified <a
                  href="/faq#link-1"
                  target="_blank"
                >lead safe or lead free</a>, you are required to have your property inspected by a certified professional licensed to operate in Pennsylvania.  Learn more about <a
                  href="https://www.phila.gov/documents/lead-and-healthy-homes-resources-for-landlords/"
                  target="_blank"
                >inspection requirements <i class="fas fa-external-link" /></a> and <a
                  href="https://www.phila.gov/services/permits-violations-licenses/apply-for-a-permit/environmental-permits-and-approvals/submit-lead-certification-and-inspection-reports/"
                  target="_blank"
                >finding a qualified lead inspector <i class="fas fa-external-link" /></a> on our website.  When you receive your results from the technician, use this site to submit proof of certification to the City.
              </li>
            </ul>
          </div>
        </div>


        <div
          v-if="$store.getters['users/unfinished']"
          class="has-text-centered"
        >
          <router-link
            tag="button"
            class="button is-primary"
            :to="{ name: 'profile' }"
          >
            Update profile.
          </router-link>
        </div>
        <template v-else>
          <p>
            Search for a residential or family daycare property using the street address or <help-icon modal="opa-number">
              OPA number
            </help-icon> to start a lead disclosure process for that address.
          </p>
          <search-property
            ref="search-property"
            class="m-t-md"
            @selected-property="initEligibilityCheck"
          />

          <!-- <hr> -->

          <!-- My properties section -->
          <div class="m-t-lg content">
            <h3
              v-text="$wording.home.title_4"
            />
            <div
              v-if="submissions.length === 0"
            >
              <p
                v-show="loadingSubmissions===true"
                v-text="$wording.home.text_6"
              />
              <p
                v-show="loadingSubmissions===false"
                v-text="$wording.home.text_7"
              />
            </div>
            <v-client-table
              v-else
              ref="allDepartments"
              :columns="tableColumns"
              :options="tableOptions"
              :data="submissions"
            >
              <template
                slot="certificationStatus"
                slot-scope="props"
              >
                <span
                  v-if="props.row.certificationStatus==='Passed'"
                  class="tag has-text-dark is-success"
                >
                  Certification finished
                </span>
                <span
                  v-else-if="props.row.certificationStatus==='Pending'"
                  class="tag has-text-dark is-info"
                >
                  Pending for review
                </span>
                <span
                  v-else-if="props.row.certificationStatus==='Draft'"
                  class="tag"
                >
                  Certification not finished
                </span>
                <span
                  v-else
                  class="tag is-danger has-text-weight-bold"
                >
                  Action required!
                </span>
                <!-- <status-tag
                  class="is-primary"
                  :status="props.row.certificationStatus"
                /> -->
              </template>
              <template
                slot="actions"
                slot-scope="props"
              >
                <button
                  class="button is-small is-secondary"
                  @click="initEligibilityDashboard(props.row)"
                >
                  <span>Open</span>
                </button>
                <button
                  v-if="props.row.certificationStatus==='Draft'"
                  class="button is-small is-outlined is-danger m-l-sm"
                  title="Remove lead disclosure"
                  @click="withdrawCertification(props.row)"
                >
                  <span
                    class="icon"
                    aria-hidden="true"
                  >
                    <i class="fas fa-trash" />
                  </span>
                </button>
              </template>
            </v-client-table>
          </div>
        </template>
      </div>
    </div>
    <more-info
      id="opa-number"
      :text="$wording.home.opa_number"
      :title="$wording.home.opa_number_title"
    />
  </div>
</template>

<script>

import SearchProperty from '@/components/SearchProperty';
import MoreInfo from '@/components/modals/MoreInfo';
import HelpIcon from '@/components/common/HelpIcon';
import { options } from '@/config/tableOptions';

export default {
  name: "SelectProperty",
  components: {
    SearchProperty,
    MoreInfo,
    HelpIcon,
  },
  data() {
    return {
      loadingSubmissions: null,
      tableOptions: options({
        sortable: [ 'submittedAt' ],
        orderBy: {
          ascending: false,
          column: 'submittedAt',
        },
        columnsClasses: {
          status: 'is-status nowrap has-text-centered',
          submittedAt: 'nowrap',
          actions: 'has-text-left actions p-sm',
        },
      }),
      // Your submissions table      
      tableColumns: [
        'opaAccountNumber',
        'opaAddress',
        'submittedAt',
        'propertyType',
        'certificationStatus',
        'actions',
      ],
    };
  },
  computed: {
    submissions() {
      return this.$store.state.properties.submissions || [];
    },
  },
  async beforeMount() {
    this.loadingSubmissions = true;
    await this.$store.dispatch('properties/getSubmissions');
    this.loadingSubmissions = false;
  },
  methods: {
    initEligibilityCheck(property) {
      // let text = this.$wording.spf(this.$wording.home.text_8, property.opa_address);
      if (this.submissions.some(
        submission => 
          submission.opaAccountNumber === property.opa_account_num
      )) {
        this.$modal.show('custom-dialog', {
          title: this.$wording.home.duplicated_property_found,
          text: this.$wording.home.text_9,
          buttons: [
            {
              title: 'Yes',
              class: 'yes-button',
              handler: () => {
                this.$modal.hide('custom-dialog');
                this.$sCache.set('selectedOPAAccountNumber', property.opa_account_num);
                this.$router.push({ path: `/submissions/new` });
              },
            },
            {
              title: 'NO',
              default: true,
              class: 'is-dark no-button',
              handler: () => {
                this.$modal.hide('custom-dialog');
              },
            },
          ],
        });
      } else {
        this.$modal.hide('custom-dialog');
        this.$sCache.set('selectedOPAAccountNumber', property.opa_account_num);
        this.$router.push({ path: `/submissions/new` });
      }
    },

    initEligibilityDashboard(eligibility) {
      this.$sCache.set('selectedOPAAccountNumber', eligibility.opaAccountNumber);
      this.$router.push({ name: `submissions-details`, params: { uuid: eligibility.id }});
    },

    async withdrawCertification(row) {
      if (await this.submitGuard('Are you sure you would like to withdraw the selected certification?')) {
        if (await this.$store.dispatch('properties/deleteSubmission', row.id)) {
          this.$store.commit('properties/removeSubmission', row.id);
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.card {
  &.is-fullheight {
    height: 100%;
  }
  &.has-bottom-button {
    .card-content {
      padding-bottom: 3rem;
      .button {
        position: absolute;
        bottom: 0;
        right: 0;
      }
    }
  }
}
</style>